/// <amd-module name="VSS/Platform/Components/Format/Format" />
import React = require("react");
import { FormatComponentProps } from "./Format.Props";

/**
 * WARNING: A copy of this component also exists in Vssf\WebPlatform\Features\VSSPreview\Scripts\VSSPreview\Flux\Components\Format.tsx.
 * Please make sure that you keep both instances in sync.
 *
 * FormatComponent is used for building tokenized formats into a completed component.
 * This is commonly used to format a localized resource string with component replacements.
 *
 * @param format formatting string with numbered replacement tokens of the form {N} where N
                 is the zero-based index of the argument to use
 * @param children arguments used for the token replacement
 */
export const FormatComponent = (props: FormatComponentProps & { children?: React.ReactNode }): JSX.Element => {
    let childrenOut: React.ReactNode[] | undefined = undefined;
    const { format, elementType, children, ...htmlProps } = props;

    if (format) {
        // This splits the format string into a sequence of [string,item#,string,item#,string]
        // For example, if format is "a {0} b {2} c {1} d", the split output will be ["a ", "0", " b ", "2", " c ", "1", " d"]
        const formatSegments = format.split(/(?:\{)([0-9]+)(?:\})/);
        const childrenIn: React.ReactNode[] = React.Children.toArray(children);
        childrenOut = [];

        // This is used to check the recurring indices
        const indicesUsed: { [index: number]: boolean } = {};

        for (let segmentIndex = 0; segmentIndex < formatSegments.length; ++segmentIndex) {
            const segment = formatSegments[segmentIndex];

            // Every other entry is either a format index or a string. The first and last are strings; odd entries will be indices.
            if (segmentIndex & 1) {
                const childIndex = parseInt(segment, 10);

                // Unlike string.format(), we don't let you use the same index more than once. If we let you use a format string
                // like "foo {0} bar {0} baz {0}...", the same child component instance would appear at multiple places in the DOM.
                // That would be bad. Throw an error to let the consumer know if an index is specified more than once.
                if (!indicesUsed[childIndex]) {
                    const childElement = childrenIn[childIndex];

                    // Make sure a child element exists for the specified index in the format
                    if (!childElement) {
                        throw new Error(`No element found for the specified index ${childIndex}`);
                    }

                    childrenOut.push(childElement);
                    indicesUsed[childIndex] = true;
                } else {
                    // An index in the format is specified more than once, let the consumer know by throwing an error
                    throw new Error(`Index in the format can only be used once, recurring index is ${childIndex}`);
                }
            } else {
                childrenOut.push(segment);
            }
        }
    }

    if (elementType === null) {
        return React.createElement(React.Fragment, null, childrenOut || children);
    }

    return React.createElement(elementType || "span", htmlProps, childrenOut || children);
};
